import { ApiError } from '@/api/client';
import { useTranslation } from '@kgc/i18n';
import { useCallback } from 'react';

/**
 * Hook to translate API errors using i18n error codes.
 *
 * Usage:
 *   const { getErrorMessage } = useApiError();
 *   catch (err) {
 *     toast.error(getErrorMessage(err));
 *   }
 *
 * If the error has a `code` field (e.g. "RENTAL_NOT_FOUND"),
 * it looks up `error.RENTAL_NOT_FOUND` in the translation file.
 * Falls back to the original error message if no translation found.
 */
export function useApiError() {
  const { t } = useTranslation();

  const getErrorMessage = useCallback(
    (err: unknown, fallback?: string): string => {
      if (err instanceof ApiError && err.code) {
        const translated = t(`error.${err.code}`);
        // If t() returns the key itself, it means no translation exists — use message fallback
        if (translated !== `error.${err.code}`) {
          return translated;
        }
        return err.message;
      }
      if (err instanceof Error) {
        return err.message;
      }
      return fallback ?? t('toast.error');
    },
    [t]
  );

  return { getErrorMessage };
}
